/** Case photos: upload in bulk to the right case (confirmed), label each photo, owner approval, folders by label. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { PHOTO_TAG_LABEL, type PhotoTag, type PhotoTagKind } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { Link, useRouter } from '../shared/router.tsx';
import { photoToJpegBase64 } from '../shared/setout.tsx';
import { Badge, Card, ErrorBox, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, day, fileBase64, refreshMailCount, when } from './common.tsx';

interface Subjects {
  people: { id: string; label: string; name: string }[];
  property: { id: string; label: string; name: string }[];
  evidence: { id: string; number: string; description: string }[];
}

const KINDS: PhotoTagKind[] = ['person', 'property', 'evidence', 'scene', 'other'];

/** Check boxes under a photo: Person / Property / Evidence / Scene / Other — and which one. */
export function TagPicker({ tags, onChange, s, idp }: { tags: PhotoTag[]; onChange: (t: PhotoTag[]) => void; s: Subjects; idp: string }) {
  const has = (k: PhotoTagKind) => tags.some((t) => t.kind === k);
  const toggle = (k: PhotoTagKind, on: boolean) => {
    if (!on) return onChange(tags.filter((t) => t.kind !== k));
    const first = k === 'person' ? s.people[0]?.id : k === 'property' ? s.property[0]?.id : k === 'evidence' ? s.evidence[0]?.id : null;
    onChange([...tags, { kind: k, ref: (k === 'person' && s.people.length === 1) || (k === 'property' && s.property.length === 1) || (k === 'evidence' && s.evidence.length === 1) ? first : null, note: null }]);
  };
  const opts = (k: PhotoTagKind) => (k === 'person' ? s.people.map((p) => [p.id, `${p.label} — ${p.name}`]) : k === 'property' ? s.property.map((p) => [p.id, `${p.label} — ${p.name}`]) : s.evidence.map((e) => [e.id, `${e.number} — ${e.description}`]));
  const disabled = (k: PhotoTagKind) => (k === 'person' && !s.people.length) || (k === 'property' && !s.property.length) || (k === 'evidence' && !s.evidence.length);
  return (
    <div className="tagp">
      <div className="tagp-boxes" role="group" aria-label="What the photo shows">
        {KINDS.map((k) => (
          <label key={k} className={`check ${disabled(k) ? 'muted' : ''}`} title={disabled(k) ? `No ${k} on this case yet` : undefined}>
            <input type="checkbox" checked={has(k)} disabled={disabled(k) && !has(k)} onChange={(e) => toggle(k, e.target.checked)} aria-label={`${PHOTO_TAG_LABEL[k]} (${idp})`} /> {PHOTO_TAG_LABEL[k]}
          </label>
        ))}
      </div>
      {tags.map((t, i) =>
        t.kind === 'person' || t.kind === 'property' || t.kind === 'evidence' ? (
          <select
            key={t.kind}
            value={t.ref ?? ''}
            aria-label={`Which ${t.kind} (${idp})`}
            onChange={(e) => onChange(tags.map((x, j) => (j === i ? { ...x, ref: e.target.value || null } : x)))}
            className={!t.ref ? 'need' : ''}
          >
            <option value="">Which {t.kind === 'person' ? 'person' : t.kind === 'property' ? 'property item' : 'evidence item'}?</option>
            {opts(t.kind).map(([id, l]) => (
              <option key={id} value={id}>
                {l}
              </option>
            ))}
          </select>
        ) : t.kind === 'other' ? (
          <input key="other" value={t.note ?? ''} placeholder="What does it show?" aria-label={`Other — what it shows (${idp})`} onChange={(e) => onChange(tags.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))} className={!t.note?.trim() ? 'need' : ''} />
        ) : null,
      )}
    </div>
  );
}

const tagsOk = (tags: PhotoTag[]) => tags.every((t) => (t.kind === 'other' ? !!t.note?.trim() : t.kind === 'scene' ? true : !!t.ref));

/** Original JPEG/PNG when it's a reasonable size (keeps the evidence untouched); otherwise a converted JPEG. */
async function photoBytes(f: File): Promise<string> {
  if ((f.type === 'image/jpeg' || f.type === 'image/png') && f.size <= 15_000_000) return fileBase64(f);
  return photoToJpegBase64(f);
}

interface Pending {
  key: string;
  file: File;
  url: string;
  tags: PhotoTag[];
  caption: string;
  state: 'ready' | 'sending' | 'sent' | 'error';
  error?: string;
}

export function PhotosPage() {
  const { path, navigate } = useRouter();
  const qs = new URLSearchParams(path.split('?')[1] ?? '');
  const [scope, setScope] = useState<'mine' | 'others'>('mine');
  const [q, setQ] = useState('');
  const [caseId, setCaseId] = useState<string | null>(qs.get('case'));
  const [confirmed, setConfirmed] = useState(false);
  const list = useLoad(() => api.get<any>(`/api/photos/cases?scope=${scope}${q.trim() ? `&q=${encodeURIComponent(q.trim())}` : ''}`), [scope, q]);
  const info = useLoad(() => (caseId ? api.get<any>(`/api/photos/case/${caseId}`) : Promise.resolve(null)), [caseId]);
  const reviewQ = useLoad(() => api.get<any>('/api/photos/review'), []);
  const presetTag = qs.get('evidence');
  const [items, setItems] = useState<Pending[]>([]);
  const [done, setDone] = useState<{ n: number; pending: boolean } | null>(null);
  const pick = useRef<HTMLInputElement>(null);
  const a = useAction();
  const c = info.data?.case;
  const s: Subjects | null = info.data ? { people: info.data.people, property: info.data.property, evidence: info.data.evidence } : null;
  useEffect(() => () => items.forEach((x) => URL.revokeObjectURL(x.url)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = (files: FileList | null) => {
    const fresh = Array.from(files ?? []).filter((f) => f.type.startsWith('image/'));
    setItems((xs) => [
      ...xs,
      ...fresh.map((f, i) => ({ key: `${Date.now()}-${i}-${f.name}`, file: f, url: URL.createObjectURL(f), tags: presetTag ? [{ kind: 'evidence' as const, ref: presetTag, note: null }] : [], caption: '', state: 'ready' as const })),
    ]);
    if (pick.current) pick.current.value = '';
  };
  const ready = items.filter((x) => x.state === 'ready' || x.state === 'error');
  const send = () =>
    a.run(async () => {
      const batchId = crypto.randomUUID();
      let n = 0;
      let pending = false;
      for (const it of ready) {
        setItems((xs) => xs.map((x) => (x.key === it.key ? { ...x, state: 'sending' } : x)));
        try {
          const r = await api.post<any>(`/api/photos/case/${caseId}`, { confirmCaseNumber: c.caseNumber, imageBase64: await photoBytes(it.file), filename: it.file.name, tags: it.tags, caption: it.caption || undefined, batchId });
          pending = pending || r.status === 'pending';
          n++;
          setItems((xs) => xs.map((x) => (x.key === it.key ? { ...x, state: 'sent' } : x)));
        } catch (e: any) {
          setItems((xs) => xs.map((x) => (x.key === it.key ? { ...x, state: 'error', error: e?.message ?? 'Upload failed' } : x)));
        }
      }
      if (pending) await api.post(`/api/photos/batch/${batchId}/done`).catch(() => undefined);
      setDone({ n, pending });
      setItems((xs) => xs.filter((x) => x.state !== 'sent'));
    });

  const reset = () => (setCaseId(null), setConfirmed(false), setItems([]), setDone(null));
  const waiting = reviewQ.data?.cases ?? [];

  return (
    <div className="ops stack photos">
      <PageHeader crumb="Incident reports" title="Upload photos" sub="Put photos on the right case and label what each one shows. Photos for another deputy’s case go in once that deputy approves them." />
      {waiting.length > 0 && <ReviewPanel cases={waiting} onDone={reviewQ.reload} />}

      {!caseId && (
        <Card title="1 · Which case are these photos for?">
          <div className="tabs" role="tablist">
            <button role="tab" aria-selected={scope === 'mine'} className={scope === 'mine' ? 'active' : ''} onClick={() => setScope('mine')}>
              My cases
            </button>
            <button role="tab" aria-selected={scope === 'others'} className={scope === 'others' ? 'active' : ''} onClick={() => setScope('others')}>
              Another deputy’s case
            </button>
          </div>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Case #, name, address or deputy" aria-label="Find the case" />
          {list.error ? (
            <ErrorBox error={list.error} />
          ) : !list.data ? (
            <Spinner />
          ) : !list.data.cases.length ? (
            <p className="muted">{scope === 'mine' ? 'You don’t have any cases yet.' : 'No cases match.'}</p>
          ) : (
            <div className="case-pick">
              {list.data.cases.map((x: any) => (
                <button key={x.id} type="button" className="case-pick-row" onClick={() => (setCaseId(x.id), setConfirmed(false))}>
                  <strong className="mono">{x.case_number}</strong>
                  <span>{x.title ?? 'Incident'}</span>
                  <span className="small muted">
                    {[x.names, x.location_text, x.occurred_at && day(x.occurred_at), scope === 'others' && x.author_name].filter(Boolean).join(' · ')}
                  </span>
                </button>
              ))}
            </div>
          )}
        </Card>
      )}

      {caseId && !confirmed && (
        <Card title="2 · Is this the right case?" className="confirm-case">
          {info.error ? (
            <ErrorBox error={info.error} />
          ) : !c ? (
            <Spinner />
          ) : (
            <>
              <dl className="kv cols">
                <dt>Case #</dt>
                <dd className="mono big-case">{c.caseNumber}</dd>
                <dt>What</dt>
                <dd>{c.title}</dd>
                <dt>Names</dt>
                <dd>{s!.people.length ? s!.people.map((p) => `${p.label}: ${p.name}`).join(' · ') : '—'}</dd>
                <dt>Location</dt>
                <dd>{c.location ?? '—'}</dd>
                <dt>When</dt>
                <dd>{c.occurredAt ? when(c.occurredAt, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}</dd>
                <dt>Report by</dt>
                <dd>
                  {c.authorName}
                  {!c.mine && <Badge tone="warn">Not your case — {c.authorName} approves the photos</Badge>}
                </dd>
              </dl>
              <p>
                <strong>
                  Is {c.caseNumber} the case you intend to upload photos to — {s!.people.slice(0, 3).map((p) => p.name).join(', ') || c.title}
                  {c.location ? `, at ${c.location}` : ''}?
                </strong>
              </p>
              <div className="row gap-sm wrap">
                <button className="btn primary" onClick={() => setConfirmed(true)}>
                  Yes, this is the right case
                </button>
                <button className="btn" onClick={reset}>
                  No — pick a different case
                </button>
              </div>
            </>
          )}
        </Card>
      )}

      {caseId && confirmed && c && s && (
        <>
          <div className="alert info confirmed-case">
            Uploading to <strong className="mono">{c.caseNumber}</strong> — {c.title}
            {c.location ? ` · ${c.location}` : ''} {c.mine ? '' : `· ${c.authorName} will approve them`}{' '}
            <button className="btn link sm" onClick={reset}>
              Change case
            </button>
          </div>
          {done && (
            <div className="alert ok">
              {done.n} photo{done.n === 1 ? '' : 's'} {done.pending ? `sent to ${c.authorName} for approval. They’ll be in the report once approved.` : 'added to the case.'}{' '}
              <Link to={at(`/photos/case/${caseId}`)}>See the case’s photo folders →</Link>
            </div>
          )}
          <Card
            title="3 · Add photos and label each one"
            actions={
              <>
                <input ref={pick} type="file" accept="image/*" multiple hidden onChange={(e) => add(e.target.files)} aria-label="Choose photos" />
                <button className="btn" onClick={() => pick.current?.click()}>
                  Choose photos
                </button>
              </>
            }
          >
            <div
              className="drop"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                add(e.dataTransfer.files);
              }}
              onClick={() => !items.length && pick.current?.click()}
            >
              {items.length === 0 ? (
                <span className="muted">Drag photos here, or tap “Choose photos”. On a phone you can take them with the camera.</span>
              ) : (
                <div className="ph-grid">
                  {items.map((it, n) => (
                    <div key={it.key} className={`ph-card ${it.state}`} onClick={(e) => e.stopPropagation()}>
                      <img src={it.url} alt={`Photo ${n + 1}`} />
                      <div className="ph-meta">
                        <span className="small muted ell">{it.file.name}</span>
                        <button className="btn link sm" onClick={() => setItems((xs) => xs.filter((x) => x.key !== it.key))} aria-label={`Remove photo ${n + 1}`}>
                          Remove
                        </button>
                      </div>
                      <TagPicker idp={`photo ${n + 1}`} s={s} tags={it.tags} onChange={(tags) => setItems((xs) => xs.map((x) => (x.key === it.key ? { ...x, tags } : x)))} />
                      <input value={it.caption} onChange={(e) => setItems((xs) => xs.map((x) => (x.key === it.key ? { ...x, caption: e.target.value } : x)))} placeholder="Caption (optional)" aria-label={`Caption for photo ${n + 1}`} />
                      {it.state === 'sending' && <span className="small">Uploading…</span>}
                      {it.state === 'error' && <span className="small bad-text">{it.error}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {items.length > 0 && (
              <div className="row gap-sm wrap">
                <span className="small muted">{ready.filter((x) => !x.tags.length).length ? `${ready.filter((x) => !x.tags.length).length} photo(s) have no label — they’ll go in “Not labeled”.` : 'Every photo is labeled.'}</span>
                <span className="spacer" />
                <button className="btn primary" disabled={a.busy || !ready.length || !ready.every((x) => tagsOk(x.tags))} onClick={send}>
                  {a.busy ? 'Uploading…' : `Upload ${ready.length} photo${ready.length === 1 ? '' : 's'} to ${c.caseNumber}`}
                </button>
              </div>
            )}
            {ready.some((x) => !tagsOk(x.tags)) && <p className="small warn-text">Pick which person / item each checked label means (or say what “Other” shows).</p>}
            <ErrorBox error={a.error} />
          </Card>
          <button className="btn ghost" onClick={() => navigate(at(`/photos/case/${caseId}`))}>
            Open this case’s photo folders
          </button>
        </>
      )}
    </div>
  );
}

/** Photos other deputies uploaded to my cases, waiting for me. */
function ReviewPanel({ cases, onDone }: { cases: any[]; onDone: () => void }) {
  const n = cases.reduce((k, c) => k + c.photos.length, 0);
  return (
    <Card title={`Waiting for your approval (${n})`} className="review-photos">
      <p className="small muted">These were uploaded to your cases by other deputies. They aren’t part of the report until you approve them. Fix the labels first if they’re wrong.</p>
      {cases.map((c) => (
        <div key={c.id} className="stack sm">
          <strong>
            <span className="mono">{c.caseNumber}</span> — {c.title}
          </strong>
          <div className="ph-grid">
            {c.photos.map((p: any) => (
              <ReviewCard key={p.id} p={p} s={c} onDone={onDone} />
            ))}
          </div>
        </div>
      ))}
    </Card>
  );
}

function ReviewCard({ p, s, onDone }: { p: any; s: Subjects; onDone: () => void }) {
  const [tags, setTags] = useState<PhotoTag[]>(p.tags);
  const [why, setWhy] = useState<string | null>(null);
  const a = useAction();
  const go = (approve: boolean) =>
    a.run(async () => {
      await api.post(`/api/photos/${p.id}/decide`, { approve, reason: why ?? undefined, tags });
      refreshMailCount();
      onDone();
    });
  return (
    <div className="ph-card">
      <a href={`/api/reports/photos/${p.id}`} target="_blank" rel="noreferrer">
        <img src={`/api/reports/photos/${p.id}`} alt={p.caption ?? 'Photo'} />
      </a>
      <span className="small muted">
        From {p.uploaded_by_name} · {day(p.created_at)}
        {p.caption ? ` · ${p.caption}` : ''}
      </span>
      <TagPicker idp={p.id.slice(0, 6)} s={s} tags={tags} onChange={setTags} />
      {why === null ? (
        <div className="row gap-sm">
          <button className="btn primary sm" disabled={a.busy || !tagsOk(tags)} onClick={() => go(true)}>
            Approve
          </button>
          <button className="btn sm" onClick={() => setWhy('')}>
            Decline…
          </button>
        </div>
      ) : (
        <div className="row gap-sm">
          <input value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Why? (wrong case, duplicate…)" aria-label="Why it’s declined" />
          <button className="btn danger sm" disabled={a.busy || !why.trim()} onClick={() => go(false)}>
            Decline
          </button>
        </div>
      )}
      <ErrorBox error={a.error} />
    </div>
  );
}

/** A case's photos in folders by label: Victim 1, Suspect 1, Property 2, Evidence E26-000004, Scene… */
export function PhotoFolders({ incidentId, embedded }: { incidentId: string; embedded?: boolean }) {
  const r = useLoad(() => api.get<any>(`/api/reports/${incidentId}/photos`), [incidentId]);
  const [open, setOpen] = useState<string | null>(null);
  const folders: any[] = r.data?.folders ?? [];
  const cur = useMemo(() => folders.find((f) => f.name === open) ?? null, [folders, open]);
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data) return <Spinner lines={2} />;
  const body = (
    <>
      {folders.length === 0 ? (
        <p className="muted small">No photos yet.</p>
      ) : (
        <div className="folders">
          {folders.map((f) => (
            <button key={f.name} type="button" className={`folder ${open === f.name ? 'on' : ''}`} onClick={() => setOpen(open === f.name ? null : f.name)}>
              <span className="folder-thumbs">
                {f.photos.slice(0, 3).map((p: any) => (
                  <img key={p.id} src={`/api/reports/photos/${p.id}`} alt="" />
                ))}
              </span>
              <strong>{f.name}</strong>
              <span className="small muted">
                {f.photos.length} photo{f.photos.length === 1 ? '' : 's'}
              </span>
            </button>
          ))}
        </div>
      )}
      {cur && (
        <div className="folder-open">
          <h3>{cur.name}</h3>
          <div className="ph-grid view">
            {cur.photos.map((p: any) => (
              <a key={p.id} className="ph-view" href={`/api/reports/photos/${p.id}`} target="_blank" rel="noreferrer">
                <img src={`/api/reports/photos/${p.id}`} alt={p.caption ?? cur.name} />
                <span className="small">{p.caption ?? p.filename ?? ''}</span>
                <span className="small muted">
                  {p.uploaded_by_name} · {day(p.created_at)}
                </span>
              </a>
            ))}
          </div>
        </div>
      )}
      {r.data.waiting.length > 0 && (
        <p className="small">
          <Badge tone="warn">{r.data.waiting.length} waiting for approval</Badge> {r.data.owner ? <Link to={at('/photos')}>Review them →</Link> : 'The report’s owner has to approve them.'}
        </p>
      )}
      {r.data.declined.length > 0 && <p className="small muted">{r.data.declined.length} declined: {r.data.declined.map((p: any) => p.decline_reason).join('; ')}</p>}
    </>
  );
  if (embedded)
    return (
      <Card
        title={`Photos (${r.data.total})`}
        actions={
          <Link className="btn sm" to={at(`/photos?case=${incidentId}`)}>
            Add photos
          </Link>
        }
      >
        {body}
      </Card>
    );
  return (
    <div className="ops stack">
      <PageHeader
        crumb={<Link to={at('/photos')}>Upload photos</Link>}
        title={`Photos — case ${r.data.caseNumber}`}
        sub={`${r.data.total} photo${r.data.total === 1 ? '' : 's'} in the report, in folders by what they show.`}
        actions={
          <Link className="btn primary" to={at(`/photos?case=${incidentId}`)}>
            Add photos
          </Link>
        }
      />
      <Card>{body}</Card>
    </div>
  );
}

